/** Set before navigating to /packages after a successful coverage check; consumed once on packages load. */
export const SHOW_PACKAGES_PLAN_TYPE_MODAL_KEY = 'cellc_show_packages_plan_type_modal';

/** Session flag: cart contains a prepaid fibre package (stepper skips vetting). */
export const CART_IS_PREPAID_SESSION_KEY = 'cellc_cart_is_prepaid';

/** Session fallback for a decision that may not be immediately visible on the re-fetched lead. */
export const VETTING_STATE_SESSION_KEY = 'cellc_vetting_state';

export enum VettingOutcome {
  Approved = 'Approved',
  Declined = 'Declined',
  Referred = 'Referred',
  Prevented = 'Prevented',
  Prepaid = 'Prepaid',
}

/** Values serialized by AgilityGIS for the latest vetting record on a lead. */
export enum CreditVettingFinalDecision {
  None = 'None',
  Approved = 'Approved',
  Pended = 'Pended',
  Declined = 'Declined',
}

/** Values serialized by AgilityGIS for the status of a vetting record. */
export enum CreditVettingStatus {
  PendingDecision = 'PendingDecision',
  PreVettingFailed = 'PreVettingFailed',
}

export const ORDER_PROCESS_ROUTES = {
  Checkout: '/checkout',
  CreditInformation: '/credit-information',
  VettingFailed: '/vetting-failed',
  VettingReferred: '/vetting-referred',
} as const;

export interface VettingSessionState {
  leadEntityID: number;
  packageEntityID: number | null;
  outcome: VettingOutcome;
}

function offerablePlanType(pkg: any): 'prepaid' | 'postpaid' | null {
  const raw = pkg?.OfferablePrepaidPostpaid ?? pkg?.CoreOfferable?.OfferablePrepaidPostpaid;
  if (raw == null || raw === '') {
    return null;
  }
  const t = String(raw).toLowerCase();
  return t === 'prepaid' || t === 'postpaid' ? t : null;
}

/** True when the package list includes at least one monthly (postpaid) and one prepaid offer. */
export function packagesListHasBothPlanTypes(packages: unknown): boolean {
  const list = Array.isArray(packages) ? packages : [];
  let hasPrepaid = false;
  let hasPostpaid = false;
  for (const pkg of list) {
    const t = offerablePlanType(pkg);
    if (t === 'prepaid') {
      hasPrepaid = true;
    } else if (t === 'postpaid') {
      hasPostpaid = true;
    }
    if (hasPrepaid && hasPostpaid) {
      return true;
    }
  }
  return false;
}

/** Same rules as checkout / installation / package-review. */
export function isPrepaidCoreOfferable(core: unknown): boolean {
  if (!core || typeof core !== 'object') {
    return false;
  }
  const c = core as Record<string, unknown>;
  const prepaidPost = (c['OfferablePrepaidPostpaid'] ?? '').toString().toLowerCase();
  if (prepaidPost === 'prepaid') {
    return true;
  }
  const cat = (c['CategoryCode'] ?? '').toString().toLowerCase();
  return cat.includes('mfn_prepaid');
}

export function isPrepaidPackage(pkg: unknown): boolean {
  if (!pkg || typeof pkg !== 'object') {
    return false;
  }
  const p = pkg as Record<string, unknown>;
  if (isPrepaidCoreOfferable(p['CoreOfferable'])) {
    return true;
  }
  return isPrepaidCoreOfferable(p);
}

/** MetroFibre (MFN) is identified by a CategoryCode containing "mfn" (same rule as packages.component). */
export function isMetroFibreCoreOfferable(core: unknown): boolean {
  if (!core || typeof core !== 'object') {
    return false;
  }
  const c = core as Record<string, unknown>;
  const cat = (c['CategoryCode'] ?? '').toString().toLowerCase();
  return cat.includes('mfn');
}

export function isMetroFibrePackage(pkg: unknown): boolean {
  if (!pkg || typeof pkg !== 'object') {
    return false;
  }
  const p = pkg as Record<string, unknown>;
  if (isMetroFibreCoreOfferable(p['CoreOfferable'])) {
    return true;
  }
  return isMetroFibreCoreOfferable(p);
}

export function setCartIsPrepaidInSession(pkg: unknown): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  if (isPrepaidPackage(pkg)) {
    sessionStorage.setItem(CART_IS_PREPAID_SESSION_KEY, '1');
  } else {
    sessionStorage.removeItem(CART_IS_PREPAID_SESSION_KEY);
  }
}

export function readCartPackageEntityIDFromSession(): number | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const cart = JSON.parse(sessionStorage.getItem('cartInfo') || 'null');
    const packageEntityID = Array.isArray(cart) ? cart[0]?.PackageEntityID : cart?.PackageEntityID;
    if (packageEntityID == null) {
      return null;
    }
    const parsed = Number(packageEntityID);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Cache an outcome for exactly one lead and package; the backend remains authoritative. */
export function setVettingStateInSession(
  leadEntityID: number,
  outcome: VettingOutcome,
  packageEntityID = readCartPackageEntityIDFromSession()
): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const parsedLeadEntityID = Number(leadEntityID);
  if (!Number.isFinite(parsedLeadEntityID)) {
    return;
  }
  const state: VettingSessionState = {
    leadEntityID: parsedLeadEntityID,
    packageEntityID,
    outcome,
  };
  sessionStorage.setItem(VETTING_STATE_SESSION_KEY, JSON.stringify(state));
}

/** Read the cached outcome only when it belongs to the current lead and package. */
export function readVettingStateFromSession(
  leadEntityID: number,
  packageEntityID = readCartPackageEntityIDFromSession()
): VettingSessionState | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const state = JSON.parse(sessionStorage.getItem(VETTING_STATE_SESSION_KEY) || 'null') as VettingSessionState | null;
    const isValid =
      state != null &&
      Number(state.leadEntityID) === Number(leadEntityID) &&
      state.packageEntityID === packageEntityID &&
      Object.values(VettingOutcome).includes(state.outcome);
    if (isValid) {
      return state;
    }
  } catch {
    // Invalid or legacy session state must never allow checkout.
  }
  clearVettingStateInSession();
  return null;
}

/** Clear cached vetting state when a new journey starts. */
export function clearVettingStateInSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(VETTING_STATE_SESSION_KEY);
}

export function getVettingOutcomeFromBackend(
  decision: unknown,
  status: unknown
): VettingOutcome | null {
  if (status === CreditVettingStatus.PreVettingFailed || decision === CreditVettingFinalDecision.Declined) {
    return VettingOutcome.Declined;
  }
  if (decision === CreditVettingFinalDecision.Approved) {
    return VettingOutcome.Approved;
  }
  if (
    decision === CreditVettingFinalDecision.Pended ||
    decision === CreditVettingFinalDecision.None ||
    status === CreditVettingStatus.PendingDecision
  ) {
    return VettingOutcome.Referred;
  }
  return null;
}

export function getRouteForVettingOutcome(outcome: VettingOutcome | null): string {
  switch (outcome) {
    case VettingOutcome.Approved:
    case VettingOutcome.Referred:
    case VettingOutcome.Prevented:
    case VettingOutcome.Prepaid:
      return ORDER_PROCESS_ROUTES.Checkout;
    case VettingOutcome.Declined:
      return ORDER_PROCESS_ROUTES.VettingFailed;
    default:
      return ORDER_PROCESS_ROUTES.CreditInformation;
  }
}

export function readCartIsPrepaidFromSession(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  if (sessionStorage.getItem(CART_IS_PREPAID_SESSION_KEY) === '1') {
    return true;
  }
  for (const key of ['packageInfo', 'selectedPackage'] as const) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw && isPrepaidPackage(JSON.parse(raw))) {
        return true;
      }
    } catch {
      /* ignore */
    }
  }
  return false;
}
